/**
 * LungChat welcome page.
 *
 * Composed from `public/index.html`, which the Python app served as a static
 * file at `/`. The chat SPA mounts under the `/lungchat` basename, so this is a
 * separate build entry rather than a router route.
 *
 * Cross-cutting behaviour: smooth in-page scrolling that also closes the mobile
 * menu (`:624-638`) and the scroll-reveal observer (`:676-690`).
 */
import { useCallback, useState } from 'react';

import { useScrollReveal } from '../../hooks/useScrollReveal';

import '../../styles/welcome.css';

import AboutSection from './AboutSection';
import ArchitectureLightbox from './ArchitectureLightbox';
import CapabilityGrid from './CapabilityGrid';
import ContactSection from './ContactSection';
import FaqSection from './FaqSection';
import HeroSection from './HeroSection';
import PaperSection from './PaperSection';
import TeamSection from './TeamSection';
import WelcomeFooter from './WelcomeFooter';
import WelcomeHeader from './WelcomeHeader';

const WelcomePage = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  useScrollReveal();

  const navigate = useCallback((hash: string) => {
    const target = document.querySelector(hash);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMenuOpen(false);
    }
  }, []);

  return (
    <>
      <WelcomeHeader
        menuOpen={menuOpen}
        onToggleMenu={() => setMenuOpen((o) => !o)}
        onNavigate={navigate}
      />
      <main>
        <HeroSection onNavigate={navigate} />
        <AboutSection onOpenArchitecture={setLightboxSrc} />
        <CapabilityGrid />
        <TeamSection />
        <FaqSection />
        <PaperSection />
        <ContactSection />
      </main>
      <WelcomeFooter />
      {lightboxSrc ? (
        <ArchitectureLightbox
          src={lightboxSrc}
          onClose={() => setLightboxSrc(null)}
        />
      ) : null}
    </>
  );
};

export default WelcomePage;
